import { useLocalStorage } from '@mantine/hooks'

const NAVIGATION_COLLAPSE_STORAGE_KEY = 'city-sense-frontend-navigation-collapsed'

export interface UseNavigationCollapseOptions {
  /**
   * Default collapsed state when no stored value exists
   * @default false
   */
  defaultCollapsed?: boolean
  /**
   * Storage key for persisting the state
   * @default 'city-sense-frontend-navigation-collapsed'
   */
  storageKey?: string
}

export interface UseNavigationCollapseReturn {
  /**
   * Current collapsed state
   */
  collapsed: boolean
  /**
   * Function to toggle the collapsed state
   */
  toggleCollapsed: () => void
  /**
   * Function to set the collapsed state
   */
  setCollapsed: (collapsed: boolean) => void
  /**
   * Function to expand the navigation
   */
  expand: () => void
  /**
   * Function to collapse the navigation
   */
  collapse: () => void
}

/**
 * Hook for managing navigation collapse state with persistent storage
 *
 * @param options Configuration options for the hook
 * @returns Object containing collapsed state and control functions
 *
 * @example
 * ```tsx
 * const { collapsed, toggleCollapsed, expand, collapse } = useNavigationCollapse()
 *
 * return (
 *   <Button onClick={toggleCollapsed}>
 *     {collapsed ? 'Expand' : 'Collapse'} Navigation
 *   </Button>
 * )
 * ```
 */
export const useNavigationCollapse = (options: UseNavigationCollapseOptions = {}): UseNavigationCollapseReturn => {
  const { defaultCollapsed = false, storageKey = NAVIGATION_COLLAPSE_STORAGE_KEY } = options

  const [collapsed, setCollapsed] = useLocalStorage<boolean>({
    key: storageKey,
    defaultValue: defaultCollapsed,
    getInitialValueInEffect: true,
  })

  const toggleCollapsed = () => {
    setCollapsed(prev => !prev)
  }

  const expand = () => {
    setCollapsed(false)
  }

  const collapse = () => {
    setCollapsed(true)
  }

  return {
    collapsed,
    toggleCollapsed,
    setCollapsed,
    expand,
    collapse,
  }
}

export default useNavigationCollapse
